import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { DataTable, EmptyState, PageHeader, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { AdminPage, ContentCard, RowAction, RowActions, apiError, useConfirm } from '../shared/AdminUi';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface OfficeRow {
    id: number;
    name: string;
    address: string;
    country: string;
    city: string;
    phone: string;
    businessModelName: string;
}

const OfficeList: React.FC<ShowAlerts> = () => {
    const t = useT();
    const notify = useNotify();
    const [confirm, confirmDialog] = useConfirm();
    const [offices, setOffices] = useState<Office[]>([]);
    const [loading, setLoading] = useState(true);
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('/admin/offices');

    setPageTitle('offices.pageTitle');
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get('offices/all');
                const list = Array.isArray(data.data) ? data.data : [];
                const sorted = [...list].sort((a: Office, b: Office) =>
                    String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'es', { sensitivity: 'base' }),
                );
                setOffices(sorted);
            } catch (error) {
                notify.error(t('offices.loadError'));
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rows: OfficeRow[] = offices.map((office: Office) => ({
        id: Number(office.id),
        name: office.name ?? '',
        phone: office.phone ?? '',
        address: office.address ?? '',
        country: office.country?.name ?? '',
        businessModelName: (office as any).businessModel?.name ?? '',
        city: office.city ?? '',
    }));

    const handleDeleteOffice = async (row: OfficeRow) => {
        if (!canDelete) return;
        const ok = await confirm({
            title: t('offices.confirmDeleteTitle', { name: row.name }),
            message: t('offices.confirmDelete'),
        });
        if (!ok) return;
        try {
            await axios.delete(`/offices/${row.id}`);
            setOffices((prev) => prev.filter((office: Office) => String(office.id) !== String(row.id)));
            notify.success(t('offices.deletedOk'));
        } catch (error) {
            notify.error(apiError(error, t('offices.deleteError')));
        }
    };

    const addButton = (
        <PermissionButton
            allowed={canCreate}
            deniedTooltip={t('common.noPermissionCreate')}
            variant="contained"
            startIcon={<AddIcon />}
            component={NavLink}
            to="/admin/offices/new"
        >
            {t('offices.newTitle')}
        </PermissionButton>
    );

    return (
        <AdminPage>
            <PageHeader title={t('offices.title')} description={t('offices.subtitle')} actions={addButton} />
            <ContentCard>
                <DataTable<OfficeRow>
                    rows={rows}
                    loading={loading}
                    getRowId={(r) => r.id}
                    ariaLabel={t('offices.title')}
                    searchable
                    searchPlaceholder={t('offices.searchPlaceholder')}
                    exportFileName={t('offices.exportFileName')}
                    initialSort={{ columnId: 'name', direction: 'asc' }}
                    columns={[
                        { id: 'name', header: t('common.name'), value: (r) => r.name, sortable: true },
                        { id: 'type', header: t('offices.type'), value: (r) => r.businessModelName, sortable: true },
                        { id: 'city', header: t('offices.city'), value: (r) => r.city, sortable: true, hideBelow: 'sm' },
                        { id: 'phone', header: t('common.cellphone'), value: (r) => r.phone, hideBelow: 'sm' },
                        { id: 'address', header: t('common.address'), value: (r) => r.address, wrap: true, width: 200, hideBelow: 'md' },
                        { id: 'country', header: t('offices.country'), value: (r) => r.country, hideBelow: 'md' },
                    ]}
                    rowActions={(row) => (
                        <RowActions>
                            <RowAction
                                label={`${t('common.edit')} ${row.name}`}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                allowed={canUpdate}
                                deniedTooltip={t('common.noPermissionEdit')}
                                to={`/admin/offices/${row.id}/edit`}
                            />
                            <RowAction
                                label={`${t('common.delete')} ${row.name}`}
                                icon={<DeleteOutlineIcon fontSize="small" />}
                                color="error"
                                allowed={canDelete}
                                deniedTooltip={t('common.noPermissionDelete')}
                                onClick={() => handleDeleteOffice(row)}
                            />
                        </RowActions>
                    )}
                    emptyState={
                        <EmptyState
                            icon={<StorefrontOutlinedIcon />}
                            title={t('offices.emptyTitle')}
                            description={t('offices.emptyDescription')}
                            action={canCreate ? addButton : undefined}
                        />
                    }
                />
            </ContentCard>
            {confirmDialog}
        </AdminPage>
    );
};

export default OfficeList;
